import React, { FC, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { AdminCreateLazyUserButton } from "kw/admin/components/AdminCreateLazyUserButton";
import { AdminCreateQuoteButton } from "./components/AdminCreateQuoteButton";
import { HeadingActionsBar } from "kanopi-components/HeadingActionBar/HeadingActionsBar";
import { KIND as BUTTON_KIND, SIZE as BUTTON_SIZE } from "kanopi-components/Button";
import { Link } from "kanopi-components/Link";
import { MenuActions } from "kanopi-components/MenuActions/MenuActions";
import { navigate } from "@reach/router";
import { TableView } from "kanopi-components/TableView/TableView";
import { useCustomers } from "./hooks/useCustomers";
import { useUser } from "kw/auth/useUser";
import { useFormatter } from "kw/util/useFormatter";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";
import { AdminCreateClaimButton } from "./components/AdminCreateClaimButton";
import { useGate } from "statsig-react";

export const CustomersPage: FC = () => {
  const { customers, loading, fetchCustomers, searchCustomers } = useCustomers();
  const { formatDate, formatString } = useFormatter();
  const user = useUser();
  const handleSearch = (searchText: string) => {
    searchCustomers(searchText);
  };
  const handlePagination = (search: string, filterBy, page: number) => {
    fetchCustomers(search, page);
  };

  const { value: isQBE, isLoading } = useGate("qbe_admin");

  const email = (customer) => {
    const ce = formatString(customer?.contactEmail);
    const e = formatString(customer?.email);

    if (e && e.includes("@")) {
      return e;
    }
    if (ce && ce.includes("@")) {
      return ce;
    }

    return "-";
  };

  const columnSchema = useMemo(() => {
    const defaultSchema = [
      {
        header: "Customer ID",
        id: "id",
        children: (row: TCustomer) => (
          <Link
            onClick={() => navigate(`/admin/customers/${row.id}`)}
            data-testid={`rowId-${row.id}`}
          >
            {row.id}
          </Link>
        ),
        sortable: false,
      },
      {
        header: "Name",
        id: "name",
        children: (row: TCustomer) => formatString(row?.fullName),
        sortable: false,
      },
      {
        header: "Email",
        id: "email",
        children: (row: TCustomer) => email(row),
        sortable: false,
      },
      {
        header: "Created",
        id: "created",
        children: (row: TCustomer) => formatDate(row?.dateJoined),
        sortable: false,
      },
      {
        id: "create-quote",
        children: (row: TCustomer) =>
          row?.isActive && (
            <MenuActions
              actions={[
                <AdminCreateQuoteButton
                  key={0}
                  customer={row}
                  kind={BUTTON_KIND.tertiary}
                  size={BUTTON_SIZE.mini}
                />,
                row?.allowClaim && (
                  <AdminCreateClaimButton
                    key={1}
                    customer={row}
                    kind={BUTTON_KIND.tertiary}
                    size={BUTTON_SIZE.mini}
                  />
                ),
              ]}
            />
          ),
        sortable: false,
      },
    ];
    const qbeSchema = [
      {
        header: "Customer ID",
        id: "id",
        children: (row: TCustomer) => (
          <Link
            onClick={() => navigate(`/admin/customers/${row.id}`)}
            data-testid={`rowId-${row.id}`}
          >
            {row.id}
          </Link>
        ),
        sortable: false,
      },
      {
        header: "Name",
        id: "name",
        children: (row: TCustomer) => formatString(row?.fullName),
        sortable: false,
      },
      {
        header: "Created",
        id: "created",
        children: (row: TCustomer) => formatDate(row?.dateJoined),
        sortable: false,
      },
      {
        id: "create-quote",
        children: (row: TCustomer) =>
          row?.isActive && (
            <MenuActions
              actions={[
                <AdminCreateQuoteButton
                  key={0}
                  customer={row}
                  kind={BUTTON_KIND.tertiary}
                  size={BUTTON_SIZE.mini}
                />,
                row?.allowClaim && (
                  <AdminCreateClaimButton
                    key={1}
                    customer={row}
                    kind={BUTTON_KIND.tertiary}
                    size={BUTTON_SIZE.mini}
                  />
                ),
              ]}
            />
          ),
        sortable: false,
      },
    ];


    return isQBE ? qbeSchema : defaultSchema;

  }, [isQBE]);


  const title = "Customers";

  useEffect(() => {
    fetchCustomers("", 1);
  }, []);

  return (
    <>
      <Helmet>
        <title>{`${title} - ${user?.organization?.name}`}</title>
      </Helmet>
      <VerticalSpacer>
        <HeadingActionsBar heading={title} />

        <TableView
          headerActions={[
            <AdminCreateLazyUserButton
              key={0}
              size={BUTTON_SIZE.compact}
              outlined={true}
            />,
          ]}
          wrapInCard={true}
          columnSchema={columnSchema}
          data={customers?.items || []}
          pageCount={customers?.pageCount || 1}
          onPageChange={handlePagination}
          currentPage={customers?.page || 1}
          loading={loading || isLoading}
          onSearch={handleSearch}
          searchPlaceholder="Search by Name or email"
          type="CUSTOMER"
        />
      </VerticalSpacer>
    </>
  );
};
